import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import socket, { configureSocketAuth } from "../../socket";
import {
  addNotification,
  getNotification,
} from "../../Redux/actions/commonActtion";
import { getProjectDetails } from "../../Redux/actions/projectActions";
import CustomToast from "../CustomToast/CustomToast";
import "../CustomToast/CustomToast.css";
import {
  getRenderHistoryPath,
  isRenderHistoryNotification,
} from "../../Pages/utils/renderHistoryNavigation";

const NOTIFICATION_SYNC_INTERVAL_MS = 10000;
const NOTIFICATION_SYNC_COOLDOWN_MS = 9000;

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const normalizeEntityId = (value) => {
  if (!value) return "";
  if (typeof value !== "object") return String(value);
  if (value._id || value.id || value.$oid) {
    return String(value._id || value.id || value.$oid);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export default function RenderCompleteListener() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentProjectId = useSelector((state) => state?.project?._id);
  const isCard = useSelector((state) => state?.project?.isCard);
  const seenNotificationKeysRef = useRef(new Set());
  const connectedUserIdRef = useRef("");
  const [activeNotice, setActiveNotice] = useState(null);
  const projectRef = useRef({ currentProjectId, isCard });
  projectRef.current = { currentProjectId, isCard };

  useEffect(() => {
    let isActive = true;

    const handleRenderUpdate = (
      data,
      { notificationAlreadyLoaded = false, refreshNotifications = true } = {},
    ) => {
      if (!data?.isRederFinished) return;

      const renderId = data?.renderId;
      const notificationKey =
        normalizeEntityId(data?.notification?._id) || normalizeEntityId(renderId);
      if (notificationKey) {
        if (seenNotificationKeysRef.current.has(notificationKey)) return;
        seenNotificationKeysRef.current.add(notificationKey);
      }

      const titleSuffix = data?.title ? ` for project ${data.title}` : "";
      const message = data?.isVideoGenerated
        ? `Your render is complete${titleSuffix}. Please check your Render History.`
        : `Your render could not be completed${titleSuffix}. Please check your Render History.`;
      const notification = {
        ...(data?.notification || {}),
        _id: data?.notification?._id || `render-${renderId || Date.now()}`,
        project_id: data?.notification?.project_id || data?.projectId,
        render_id: data?.notification?.render_id || renderId,
        notification_type: "render-complete",
        is_card: data?.notification?.is_card ?? Boolean(data?.isCard),
        is_video_generated:
          data?.notification?.is_video_generated ?? Boolean(data?.isVideoGenerated),
        message: data?.notification?.message || message,
        createdAt: data?.notification?.createdAt || new Date().toISOString(),
      };
      const renderHistoryPath = getRenderHistoryPath(notification);

      if (!notificationAlreadyLoaded) dispatch(addNotification(notification));
      if (refreshNotifications) {
        dispatch(getNotification({ page_no: 1, limit: 20, append: false }));
      }
      setActiveNotice({ notification, renderHistoryPath });

      const { currentProjectId: openId, isCard: openIsCard } =
        projectRef.current;
      if (openId && data?.projectId && String(openId) === String(data.projectId)) {
        dispatch(getProjectDetails(openId, openIsCard));
      }
    };

    const fetchSavedNotifications = async () => {
      try {
        const notifications = await dispatch(
          getNotification({
            page_no: 1,
            limit: 20,
            append: false,
            silent: true,
          }),
        );
        return Array.isArray(notifications) ? notifications : [];
      } catch {
        return [];
      }
    };

    const primeNotifications = fetchSavedNotifications().then((notifications) => {
      if (!isActive || !Array.isArray(notifications)) return;
      notifications.forEach((notification) => {
        const notificationKey =
          normalizeEntityId(notification?._id) ||
          normalizeEntityId(notification?.render_id);
        if (notificationKey) seenNotificationKeysRef.current.add(notificationKey);
      });
    });

    let syncPromise = null;
    let lastSyncAt = 0;
    const syncSavedRenderNotifications = async () => {
      await primeNotifications;
      if (!isActive) return;
      if (syncPromise) return syncPromise;
      if (Date.now() - lastSyncAt < NOTIFICATION_SYNC_COOLDOWN_MS) return;
      lastSyncAt = Date.now();

      syncPromise = fetchSavedNotifications()
        .then((notifications) => {
          if (!isActive || !Array.isArray(notifications)) return;
          notifications
            .filter(isRenderHistoryNotification)
            .forEach((notification) => {
              handleRenderUpdate(
                {
                  isRederFinished: true,
                  renderId: notification.render_id,
                  projectId: notification.project_id,
                  isVideoGenerated: notification.is_video_generated,
                  isCard: notification.is_card,
                  notification,
                },
                {
                  notificationAlreadyLoaded: true,
                  refreshNotifications: false,
                },
              );
            });
        })
        .finally(() => {
          syncPromise = null;
        });

      return syncPromise;
    };

    const handleConnect = () => {
      const user = getStoredUser();
      if (!user?._id) return;
      connectedUserIdRef.current = normalizeEntityId(user._id);
      void syncSavedRenderNotifications();
    };

    const handleDisconnect = () => {
      void syncSavedRenderNotifications();
    };

    const connectForUser = () => {
      const user = getStoredUser();
      if (!user?._id) {
        if (socket.connected) socket.disconnect();
        connectedUserIdRef.current = "";
        return;
      }

      const userId = normalizeEntityId(user._id);
      if (socket.connected && connectedUserIdRef.current === userId) {
        void syncSavedRenderNotifications();
        return;
      }

      if (socket.connected) socket.disconnect();
      if (!configureSocketAuth()) return;
      connectedUserIdRef.current = userId;
      socket.connect();
    };

    socket.on("render-update", handleRenderUpdate);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    connectForUser();
    const notificationSyncTimer = window.setInterval(() => {
      if (document.visibilityState === "visible" && getStoredUser()?._id) {
        void syncSavedRenderNotifications();
      }
    }, NOTIFICATION_SYNC_INTERVAL_MS);

    window.addEventListener("storage", connectForUser);
    window.addEventListener("focus", connectForUser);

    return () => {
      isActive = false;
      socket.off("render-update", handleRenderUpdate);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      window.clearInterval(notificationSyncTimer);
      window.removeEventListener("storage", connectForUser);
      window.removeEventListener("focus", connectForUser);
    };
  }, [dispatch, navigate]);

  return (
    activeNotice && (
      <div className="render-complete-notice-layer" role="status" aria-live="polite">
        <div className="render-complete-notice">
          <CustomToast
            title=""
            content={activeNotice.notification.message}
            actionLabel={
              activeNotice.renderHistoryPath ? "View Render History" : undefined
            }
            onAction={
              activeNotice.renderHistoryPath
                ? () => {
                    navigate(activeNotice.renderHistoryPath);
                    setActiveNotice(null);
                  }
                : undefined
            }
          />
          <button
            type="button"
            className="render-complete-notice-close"
            aria-label="Dismiss render notification"
            onClick={() => setActiveNotice(null)}
          >
            ✖
          </button>
        </div>
      </div>
    )
  );
}
